<template>
  <div>
    <slot name="activator" v-bind:onShowModal="onShowModal">
      <b-button @click="onShowModal" variant="success" size="sm">Crear</b-button>
    </slot>

    <b-modal
      v-model="modal"
      centered
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      dialog-class="user-form__dialog"
    >
      <template #modal-title>
        <span :class="isEditingUser ? 'text-danger' : ''">{{ showFormTitle }}</span>
      </template>

      <x-alert-with-errors
        :error="response.error"
        :title="response.message"
        :errors="response.errors"
      />

      <div v-if="isEditingUser" class="user-form__meta">
        <span v-text="getLastAccessText"></span>
        <span v-text="getCreationDate"></span>
        <span v-text="getModificationDate"></span>
      </div>

      <b-overlay :show="isLoading">
        <template #overlay>
          <div class="text-center">
            <loading message="Procesando información, por favor espere..." />
          </div>
        </template>

        <div :class="{ isLoading }">
          <FormulateForm
            ref="userForm"
            name="userForm"
            v-model="formValues"
            :errors="response.errors"
            @submit="submit"
          >
            <b-tabs small content-class="pt-3" class="user-form__tabs">
            <!-- Pestaña: datos generales -->
            <b-tab title="Datos" active>
            <b-row>
              <!-- Nombre -->
              <b-col cols="12" md="12">
                <FormulateInput
                  name="nombre"
                  label="Nombre(s)"
                  required
                  type="text"
                  validation="required"
                  validation-name="Nombre(s)"
                />
              </b-col>

              <!-- Primer apellido -->
              <b-col cols="12" md="6">
                <FormulateInput
                  name="primer_apellido"
                  label="Apellido paterno"
                  required
                  type="text"
                  validation="bail|required"
                  validation-name="Apellido paterno"
                />
              </b-col>

              <!-- Segundo apellido -->
              <b-col cols="12" md="6">
                <FormulateInput
                  name="segundo_apellido"
                  label="Apellido materno"
                  required
                  type="text"
                  validation="bail|required"
                  validation-name="Apellido materno"
                />
              </b-col>
            </b-row>

            <b-row>
            <b-col cols="12" md="6">
            <!-- Profesion -->
            <FormulateInput
              name="profesion"
              label="Profesión"
              required
              type="text"
              placeholder="Lic., Ing., C.P."
              validation="bail|required"
              validation-name="Profesión"
            />
            </b-col>
            <b-col cols="12" md="6">
            <!-- Fecha ingreso -->
            <FormulateInput
              name="fecha_ingreso"
              label="Fecha de ingreso"
              type="date"
              required
              validation="required"
              validation-name="Fecha de ingreso"
            />
            </b-col>
            </b-row>

            <!-- Email -->
            <FormulateInput
              name="email"
              label="Correo electrónico"
              required
              type="email"
              validation="bail|required|email"
              validation-name="Correo electrónico"
              error-behavior="live"
            >
              <template #element>
                <b-input-group>
                  <input
                    v-model="formValues.email"
                    type="email"
                    @blur="checkDisabledEmail"
                    placeholder="usuario@instcamp.edu.mx"
                    class="form-control"
                  />
                  <template #append v-if="isEditingUser && passwordLogin">
                    <b-button
                      @click="sendResetPasswordEmail(formValues.email)"
                      size="sm"
                      :variant="recoverPassword.variant"
                      v-b-tooltip.hover
                      :disabled="recoverPassword.sending"
                      :title="
                        recoverPassword.sending ? 'Enviando correo...' : recoverPassword.message
                      "
                    >
                      <b-icon-envelope></b-icon-envelope>
                    </b-button>
                  </template>
                </b-input-group>
              </template>
            </FormulateInput>

            <!-- Contraseña -->
            <FormulateInput
              v-if="!isEditingUser && passwordLogin"
              name="password"
              label="Contraseña"
              required
              autocomplete="on"
              error-behavior="live"
              validation="bail|required|min:10,length|max:32,length|noRepeatingCharacters|notConsecutiveNumbers|atLeastOneMayus|atLeastOneSymbol|atLeastOneNumber"
              :validation-rules="$getPasswordValidationRules"
              :validation-messages="$getPasswordValidationMessages"
            >
              <template #element>
                <div class="input-group">
                  <div class="input-group-prepend">
                    <b-button @click="getGeneratedPassword" variant="primary">Generar</b-button>
                  </div>
                  <input
                    :value="formValues.password"
                    :type="showingPassword.value ? 'text' : 'password'"
                    readonly
                    class="form-control"
                  />
                  <div class="input-group-append">
                    <b-button
                      @click="toggleShowPassword"
                      variant="danger"
                      size="sm"
                      v-b-tooltip.hover
                      :title="showingPassword.text"
                    >
                      <b-icon-eye v-if="showingPassword.value"></b-icon-eye>
                      <b-icon-eye-slash v-else></b-icon-eye-slash>
                    </b-button>
                  </div>
                </div>
              </template>
            </FormulateInput>

            </b-tab>

            <!-- Pestaña: unidad administrativa y capítulos -->
            <b-tab title="Adscripción">
            <!-- Uniades administrativas -->
            <FormulateInput
              name="unidades_administrativas"
              label="Unidades administrativas"
              required
              validation="bail|required"
              validation-name="Unidades administrativas"
              error-behavior="live"
            >
              <template #element>
                <check-authorization :requiresAuthorizations="['listar unidades administrativas']">
                  <v-select
                    class="style-chooser"
                    v-model="formValues.unidades_administrativas"
                    :reduce="unidades_administrativas => unidades_administrativas.value"
                    :options="getAdministrativeUnitsForForms"
                  />
                </check-authorization>
              </template>
            </FormulateInput>

            <!-- Capítulos -->
            <FormulateInput
              name="capitulos"
              label="Capítulos"
              required
              validation="bail|required"
              validation-name="Capítulos"
              error-behavior="live"
            >
              <template #element>
                <v-select
                  class="style-chooser"
                  v-model="formValues.capitulos"
                  multiple
                  :reduce="capitulos => capitulos.value"
                  :options="getListForForms"
                />
              </template>
            </FormulateInput>

            </b-tab>

            <!-- Pestaña: roles -->
            <b-tab>
              <template #title>
                Roles <b-badge variant="light" pill>{{ selectedRoleNames.length }}</b-badge>
              </template>
              <check-authorization :requiresAuthorizations="['listar roles']">
                <p class="small text-muted mb-2">
                  Los permisos los da cada rol. "admin" tiene acceso a todo el sistema.
                </p>
                <b-form-checkbox-group v-model="selectedRoleNames" stacked class="user-form__roles">
                  <b-form-checkbox v-for="role in getSystemRoles" :key="role.name" :value="role.name">
                    {{ role.name }}
                  </b-form-checkbox>
                </b-form-checkbox-group>
              </check-authorization>
            </b-tab>
            </b-tabs>
          </FormulateForm>
        </div>
      </b-overlay>

      <template #modal-footer>
        <!-- Enviar para crear o actualizar -->
        <div class="w-100">
          <x-form-footer-buttons
            :disabled="$refs.userForm ? $refs.userForm.hasErrors : false"
            :loading="isLoading"
            :isEditing="isEditingUser"
            :isCreating="!isEditingUser"
            @on-cancel="resetForm"
            @on-create="submit"
            @on-update="submit"
          ></x-form-footer-buttons>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

const formValues = () => ({
  nombre: '',
  primer_apellido: '',
  segundo_apellido: '',
  profesion: '',
  unidades_administrativas: null,
  capitulos: null,
  fecha_ingreso: null,
  email: '',
  password: null,
  roles: [],
  roles_checklist: []
})

const showingPassword = () => ({
  value: false,
  text: 'Mostrar contraseña',
  type: 'password'
})

const recoverPassword = () => ({
  sending: false,
  message: 'Enviar email para recuperación de contraseña',
  variant: 'warning'
})

const response = () => ({
  error: false,
  message: '',
  errors: {}
})

export default {
  name: 'UserForm',

  components: {
    vSelect
  },

  props: {
    value: {
      type: Boolean,
      required: true
    }
  },

  data () {
    return {
      modal: this.value,

      isLoading: false,

      // Último correo revisado contra los deshabilitados, para no preguntar dos veces.
      emailChecked: null,

      formValues: formValues(),

      selectedRoleNames: [],

      // Los catálogos (roles, unidades y capítulos) se piden la primera vez que se abre el modal.
      catalogsLoaded: false,

      // Login con contraseña desactivado: solo Google (VUE_APP_PASSWORD_LOGIN=true lo reactiva).
      passwordLogin: process.env.VUE_APP_PASSWORD_LOGIN === 'true',

      showingPassword: showingPassword(),

      recoverPassword: recoverPassword(),

      response: response()
    }
  },

  computed: {
    //
    // User module
    ...mapState('userModule', ['editingUser']),
    ...mapGetters('userModule', ['getRolesOfEditingUser', 'isEditingUser']),

    //
    // Role module
    ...mapGetters('roleModule', ['getSystemRoles']),

    //
    // Administrative Unit Module
    ...mapGetters('administrativeUnitModule', [
      'getAdministrativeUnitsForForms',
      'hasAdministrativeUnits'
    ]),

    //
    // Capítulos Unit Module
    ...mapGetters('capitulosModule', [
      'getListForForms',
      'hasList'
    ]),

    showFormTitle () {
      return this.isEditingUser
        ? `Editando usuario: '${this.editingUser.persona.nombre}'`
        : 'Crear nuevo usuario'
    },

    getLastAccessText () {
      if (!this.isEditingUser) return ''

      return this.editingUser.ultimo_acceso === 'Sin registro de ingreso'
        ? this.editingUser.ultimo_acceso
        : `Último acceso: ${this.$formatDate(this.editingUser.ultimo_acceso)}`
    },

    getCreationDate () {
      if (!this.isEditingUser) return ''

      return `Creación: ${this.$customFormatDate(this.editingUser.fecha_creacion, 'DD/MMMM/YYYY')}`
    },

    getModificationDate () {
      if (!this.isEditingUser) {
        return ''
      }

      return `Modificación: ${this.$customFormatDate(
        this.editingUser.ultima_modificacion,
        'DD/MMMM/YYYY'
      )}`
    }
  },

  watch: {
    isEditingUser: {
      handler (value) {
        this.recoverPassword = recoverPassword()

        if (value) {
          const { id, email, persona, empleado, roles_checklist } = this.editingUser
          this.formValues.id = id
          this.formValues.nombre = persona.nombre
          this.formValues.primer_apellido = persona.primer_apellido
          this.formValues.segundo_apellido = persona.segundo_apellido

          this.formValues.profesion = empleado.profesion

          //
          // Unidades administrativas
          //
          // Explícitamente obtiene el id de la unidad administrativa para el XSelect
          this.formValues.unidades_administrativas =
            empleado.unidades_administrativas_nombres.length > 0
              ? empleado.unidades_administrativas_nombres[0].id
              : null

          //
          // Capítulos
          //
          // Explícitamente obtiene el id del capítulo para el XSelect
          this.formValues.capitulos =
            empleado.capitulos_nombres.length > 0
              ? empleado.capitulos_nombres[0].id
              : null

          //
          // Fecha de ingreso
          this.formValues.fecha_ingreso = moment(empleado.fecha_ingreso).format('YYYY-MM-DD')

          //
          // Email
          this.formValues.email = email

          //
          // Se añade la lista de roles que posee
          this.formValues.roles_checklist = roles_checklist
          this.selectedRoleNames = roles_checklist.map(r => r.name)

          return
        }

        this.resetForm()
      }
    },

    value: {
      immediate: true,
      handler (value) {
        this.modal = value
        this.getGeneratedPassword()
      }
    },

    modal: {
      immediate: true,
      handler (value) {
        if (value) this.loadCatalogs()
        this.$emit('input', value)
      }
    }
  },

  methods: {
    async loadCatalogs () {
      if (this.catalogsLoaded) return
      this.catalogsLoaded = true

      //
      // Obtiene todos los roles del sistema
      this.$store.dispatch('roleModule/getSystemRoles')

      //
      // Obtiene las unidades administrativas y capítulos de acuerdo al período fiscal activo
      await this.$store.dispatch('fiscalPeriodsModule/getActiveFiscalPeriods')
      const period = await this.$getPeriodoFiscalActivo()
      if (!period) return

      this.$store.dispatch('administrativeUnitModule/getAdministrativeUnits', period.id)
      this.$store.dispatch('capitulosModule/getAll', period.id)
    },

    /**
     * Envía el formulario.
     *
     * Envía el formulario para su creación o su actualización.
     * Dependiendo si la variable "isEditingUser" es verdadera actualizará
     * si no es así entonces mandará crear.
     */
    async submit () {
      this.response = response()
      this.prepareInput()

      this.isLoading = true
      const result = this.isEditingUser
        ? await this.updateUser(this.formValues)
        : await this.createUser(this.formValues)
      this.isLoading = false

      // El correo es de alguien deshabilitado: se ofrece reactivarlo.
      if (result.error && result.code === 409 && result.data) return this.offerRestore(result)

      const { error, message, data } = result

      this.$notify({ error, message }, 'Envío de formulario')

      this.response.error = error
      this.response.message = message
      this.response.errors = error ? data.errors : {}

      if (!error) this.resetForm()
    },

    // Al salir del correo en un alta: si es de alguien deshabilitado se avisa
    // de una vez. Se pregunta una sola vez por correo; al guardar vuelve a revisarse.
    async checkDisabledEmail () {
      const email = (this.formValues.email || '').trim()
      if (this.isEditingUser || !email || email === this.emailChecked) return

      this.emailChecked = email
      const data = await this.$store.dispatch('userModule/findDisabledByEmail', email)
      if (data && !this.isEditingUser) this.offerRestore({ data })
    },

    async offerRestore ({ data }) {
      const desde = data.deshabilitado_desde
        ? ` desde el ${moment(data.deshabilitado_desde).format('DD [de] MMMM [de] YYYY')}`
        : ''
      const ok = await this.$bvModal.msgBoxConfirm(
        `El correo ${data.email} ya pertenece a ${data.nombre || 'un usuario'}, que está deshabilitado${desde}. ` +
          '¿Lo vuelvo a habilitar? Conserva sus datos y roles anteriores, y enseguida se abre para que los revises.',
        {
          title: 'Usuario existente',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'success',
          okTitle: 'Habilitar y editar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          centered: true
        }
      )

      if (!ok) {
        this.response.error = true
        this.response.message = 'Ese correo ya existe en un usuario deshabilitado.'
        return
      }

      const restored = await this.$store.dispatch('userModule/restoreUser', data.id)
      this.$notify({ error: restored.error, message: restored.message }, 'Usuarios')
      if (restored.error) return

      this.resetForm()
      await this.$store.dispatch('userModule/getUserToEdit', data.id)
    },

    async createUser (form) {
      return await this.$store.dispatch('userModule/createUser', form)
    },

    async updateUser (form) {
      return await this.$store.dispatch('userModule/updateUser', {
        id: form.id,
        payload: form
      })
    },

    /**
     * Prepara los datos antes de enviarlos al backend.
     *
     * Prepara los datos necesarios como la fecha o los roles y permisos que
     * se deben procesar en el backend.
     */
    prepareInput () {
      this.formValues.fecha_ingreso = moment(this.formValues.fecha_ingreso).format('YYYY-MM-DD')

      //
      // Obtiene los roles seleccionados
      this.getSelectedRoles()
    },

    /**
     * Si el usuario actual no tiene permisos para listar los roles,
     * entonces se toman los roles por defecto del usuario que está editando.
     */
    getSelectedRoles () {
      if (!this.$can(['listar roles'])) {
        this.formValues.roles = this.getRolesOfEditingUser
        return
      }

      //
      // Se necesita mandar los roles, en formato de array, únicamente los nombres.
      this.formValues.roles = [...this.selectedRoleNames]
    },

    getGeneratedPassword () {
      const password = this.$onGeneratePassword()
      this.formValues.password = password
      this.$forceUpdate()
    },

    toggleShowPassword () {
      this.showingPassword.value = !this.showingPassword.value
      this.showingPassword.text = this.showingPassword.value
        ? 'Ocultar contraseña'
        : 'Mostrar contraseña'
      this.showingPassword.type = this.showingPassword.value ? 'text' : 'password'
    },

    resetForm () {
      document.activeElement.blur()
      this.$store.commit('userModule/setUserToEdit', null)
      this.$formulate.reset('userForm')
      this.formValues = formValues()
      this.response = response()
      this.showingPassword = showingPassword()
      this.selectedRoleNames = []
      this.recoverPassword = recoverPassword()
      this.emailChecked = null

      this.modal = false
    },

    async sendResetPasswordEmail (email) {
      this.recoverPassword.sending = true

      const getval = await this.$store.dispatch('authModule/recoverPassword', email)

      this.$notify(getval, 'Envio de email')

      this.recoverPassword.sending = false
      this.recoverPassword.message = getval.error
        ? 'Reintentar enviar correo de recuperación de contraseña'
        : getval.message
      this.recoverPassword.variant = getval.error ? 'danger' : 'success'
    },

    onInputFechaIngreso (value) {
      this.formValues.fecha_ingreso = moment(value).format('YYYY-MM-DD')
    },

    onShowModal () {
      this.modal = true
      this.$emit('input', this.modal)
    }
  }
}
</script>

<style>
.user-form__dialog {
  max-width: 720px;
}

.user-form__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
  font-size: 0.75rem;
  color: #69707a;
  margin-bottom: 0.75rem;
}

.user-form__roles {
  column-count: 2;
  font-size: 0.85rem;
}

.user-form__roles .custom-control {
  break-inside: avoid;
  margin-bottom: 0.35rem;
}

.required_form {
  color: red;
}
.isLoading {
  max-height: 400px !important;
  overflow: hidden;
}
</style>
